import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { ShieldCheck, Zap, Waves, Ticket, ArrowRight, Anchor } from "lucide-react";
import { Navbar } from "@/components/Navbar";
import { Footer } from "@/components/Footer";
import { SearchForm } from "@/components/SearchForm";
import { Button } from "@/components/ui/button";
import api, { inr } from "@/lib/api";

const HERO = "https://images.unsplash.com/photo-1778353536923-11b0120106db?crop=entropy&cs=srgb&fm=jpg&q=85&w=2000";

export default function Landing() {
  const [routes, setRoutes] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    api.get("/routes").then((r) => setRoutes(r.data)).catch(() => {});
  }, []);

  const openRoute = (r) => {
    const d = new Date().toISOString().slice(0, 10);
    navigate(`/search?from=${r.from_port_id}&to=${r.to_port_id}&date=${d}&pax=1`);
  };
  const premium = [...routes].sort((a, b) => (b.from_price || 0) - (a.from_price || 0)).slice(0, 15);
  const popular = [...routes].sort((a, b) => (a.from_price || 0) - (b.from_price || 0)).slice(0, 6);

  const renderCard = (r, i) => (
    <button
      key={r.id}
      onClick={() => openRoute(r)}
      className="card-lift group text-left bg-white rounded-xl overflow-hidden border border-border animate-rise"
      style={{ animationDelay: `${i * 0.05}s` }}
      data-testid={`route-card-${r.id}`}
    >
      <div className="h-44 overflow-hidden relative">
        <img src={r.image_url} alt={r.to_port?.city} className="h-full w-full object-cover group-hover:scale-105 transition-transform duration-300" />
        {r.from_price >= 45000 && (
          <span className="absolute top-3 left-3 bg-accent text-white text-[11px] font-semibold px-2.5 py-1 rounded-full" data-testid={`luxury-tag-${r.id}`}>
            Luxury cruise
          </span>
        )}
      </div>
      <div className="p-5">
        <div className="flex items-center gap-2 text-primary font-display font-semibold text-lg">
          {r.from_port?.city} <ArrowRight className="h-4 w-4 text-accent" /> {r.to_port?.city}
        </div>
        <p className="text-sm text-muted-foreground mt-1">{r.from_port?.name} → {r.to_port?.name}</p>
        <div className="mt-4 flex items-center justify-between">
          <span className="text-sm text-muted-foreground">from</span>
          <span className="font-display font-bold text-primary text-lg">{r.from_price ? inr(r.from_price) : "—"}</span>
        </div>
      </div>
    </button>
  );

  return (
    <div className="min-h-screen bg-background">
      <Navbar transparent />

      {/* Hero */}
      <section className="relative bg-primary overflow-hidden">
        <img src={HERO} alt="Ferry on ocean" className="absolute inset-0 h-full w-full object-cover" />
        <div className="absolute inset-0 bg-primary/60" />
        <div className="relative max-w-7xl mx-auto px-5 sm:px-8 pt-20 pb-40 sm:pt-28 sm:pb-48">
          <p className="overline text-accent-foreground/90 text-accent mb-4 animate-rise">Coastal & harbour ferries · India</p>
          <h1 className="font-display font-extrabold text-white tracking-tighter text-4xl sm:text-5xl lg:text-6xl max-w-3xl animate-rise" style={{ animationDelay: "0.05s" }}>
            Sail the coast. Skip the queue.
          </h1>
          <p className="mt-5 text-white/85 text-base sm:text-lg max-w-xl animate-rise" style={{ animationDelay: "0.1s" }}>
            Compare schedules, pick your class and confirm your ferry ticket in under a minute — from Mumbai harbour to the Kerala backwaters.
          </p>
        </div>
      </section>

      {/* Floating search */}
      <div className="max-w-6xl mx-auto px-5 sm:px-8 -mt-28 relative z-10">
        <SearchForm />
      </div>

      {/* Premium cruises */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-24">
        <div className="flex items-end justify-between mb-8">
          <div>
            <p className="overline text-accent mb-2">Premium cruises</p>
            <h2 className="font-display font-bold text-3xl text-primary tracking-tight">Signature big-ticket sailings</h2>
          </div>
          <Anchor className="h-8 w-8 text-primary/20 hidden sm:block" strokeWidth={1.5} />
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {premium.map((r, i) => renderCard(r, i))}
        </div>
      </section>

      {/* Everyday routes */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-20">
        <div className="mb-8">
          <p className="overline text-accent mb-2">Everyday routes</p>
          <h2 className="font-display font-bold text-3xl text-primary tracking-tight">Coastal hops from ₹5,000</h2>
        </div>
        <div className="grid gap-5 sm:grid-cols-2 lg:grid-cols-3">
          {popular.map((r, i) => renderCard(r, i))}
        </div>
      </section>

      {/* Features */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-24">
        <div className="grid gap-6 md:grid-cols-3">
          {[
            { icon: Zap, t: "Instant confirmation", d: "Tickets delivered the moment your payment clears. No waiting, no counters." },
            { icon: ShieldCheck, t: "Secure payments", d: "Checkout powered by Cashfree with bank-grade encryption on every booking." },
            { icon: Waves, t: "Live schedules", d: "Accurate departure and arrival times across every operator we list." },
          ].map((f) => (
            <div key={f.t} className="bg-white rounded-xl border border-border p-8">
              <span className="h-11 w-11 rounded-md bg-primary/5 flex items-center justify-center mb-5">
                <f.icon className="h-5 w-5 text-accent" strokeWidth={1.75} />
              </span>
              <h3 className="font-display font-semibold text-lg text-primary">{f.t}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{f.d}</p>
            </div>
          ))}
        </div>
      </section>

      {/* CTA */}
      <section className="max-w-7xl mx-auto px-5 sm:px-8 mt-24">
        <div className="bg-primary rounded-2xl px-8 py-14 sm:px-14 relative overflow-hidden">
          <div className="relative z-10 max-w-lg">
            <Ticket className="h-8 w-8 text-accent mb-4" strokeWidth={1.5} />
            <h2 className="font-display font-bold text-white text-3xl tracking-tight">Your next crossing is one search away.</h2>
            <p className="text-white/75 mt-3">Create a free account to manage bookings and get instant e-tickets.</p>
            <Button className="mt-6 bg-accent hover:bg-accent/90 text-white font-semibold" onClick={() => navigate("/register")} data-testid="cta-register-btn">
              Create free account <ArrowRight className="h-4 w-4 ml-2" />
            </Button>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
